/**
 * v-can — hide an element unless the signed-in staff member meets a
 * permission requirement. Reactive: a permission refresh (socket
 * `permissions:changed`, version header) shows/hides it without a reload.
 *
 *   <button v-can="'tasks.create'">New task</button>
 *   <div v-can="['leads.view', 'clients.view']">…</div>            anyOf
 *   <button v-can="{ slug: 'payroll.view', scope: 'all' }">…</button>
 *   <button v-can="{ allOf: ['invoices.create', 'payments.create'] }">…</button>
 *
 * Hides with `hidden` plus an inline `display: none !important`, so utility
 * classes such as `flex` can't override it. Prefer `v-if="can(...)"` (or
 * <Can>) when the hidden markup must not exist at all.
 */
import { shallowRef, watchEffect, type ObjectDirective, type ShallowRef, type WatchStopHandle } from 'vue';
import { useAuthStore } from '../stores/auth';
import type { CanValue } from './types';

interface CanState {
  value: ShallowRef<CanValue | null | undefined>;
  stop: WatchStopHandle;
}

const STATE = new WeakMap<HTMLElement, CanState>();

function apply(el: HTMLElement, allowed: boolean, originalDisplay: string): void {
  el.hidden = !allowed;
  if (!allowed) {
    el.style.setProperty('display', 'none', 'important');
  } else if (originalDisplay) {
    el.style.display = originalDisplay;
  } else {
    el.style.removeProperty('display');
  }
}

export const vCan: ObjectDirective<HTMLElement, CanValue | null | undefined> = {
  mounted(el, binding) {
    const auth = useAuthStore();
    const value = shallowRef(binding.value);
    const originalDisplay = el.style.display;
    const stop = watchEffect(() => apply(el, auth.meets(value.value), originalDisplay));
    STATE.set(el, { value, stop });
  },
  updated(el, binding) {
    const state = STATE.get(el);
    if (state && binding.value !== binding.oldValue) state.value.value = binding.value;
  },
  unmounted(el) {
    STATE.get(el)?.stop();
    STATE.delete(el);
  },
};
